import { Fragment } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import { Icon } from "../Icon/Icon";
import type { UiTheme } from "../theme";
import type { SettingsMenuSection } from "./settingsMenu";

// The Settings menu — a grouped index of sub-page rows, shared by mobile's
// ProfileScreen and the web-rnw console. Chrome-less on purpose: it drops
// into mobile's SafeAreaView+ScrollView or the web master-detail's list
// pane unchanged. The mount owns navigation (onSelect(key) → push / detail
// / modal); this view only renders + signals.
//
// `selectedKey` drives the wide master-detail highlight (the active row in
// the left pane); mobile omits it (native push, no persistent selection).
// The highlight is never colour-alone — it pairs a brand tint with
// accessibilityState.selected so a screen reader and reduced-vision users
// read the state too.

export type SettingsMenuViewProps = {
  theme: UiTheme;
  sections: SettingsMenuSection[];
  /** Wide master-detail: the currently-open sub-page. Omit on mobile. */
  selectedKey?: string | null;
  onSelect: (key: string) => void;
  /**
   * Slim title-only nav for the wide master-detail pane: drops the row
   * descriptions + custom slots and tightens the rows, so the persistent
   * settings sidebar doesn't eat a content-width column. Mobile + the
   * narrow web menu leave it off (full rows with descriptions).
   */
  dense?: boolean;
};

export function SettingsMenuView({
  theme,
  sections,
  selectedKey,
  onSelect,
  dense = false,
}: SettingsMenuViewProps) {
  const styles = makeStyles(theme);

  return (
    <View style={[styles.list, dense && styles.listDense]}>
      {sections.map((section) => (
        <Fragment key={section.label}>
          <Text style={styles.sectionHeader}>{section.label}</Text>
          {section.rows.map((row) => {
            const selected = selectedKey != null && row.key === selectedKey;
            return (
              <Pressable
                key={row.key}
                accessibilityRole="button"
                accessibilityState={{ selected }}
                onPress={() => onSelect(row.key)}
                style={({ pressed }) =>
                  dense
                    ? [
                        styles.denseRow,
                        selected && styles.denseRowSelected,
                        pressed && !selected && styles.denseRowPressed,
                      ]
                    : [
                        styles.navRow,
                        selected && styles.navRowSelected,
                        pressed && !selected && styles.navRowPressed,
                      ]
                }
              >
                {dense ? (
                  <>
                    {/* Active cue = shape (accent bar) + weight + colour,
                        mirroring the console rail; never colour alone. */}
                    <View style={[styles.denseAccent, selected && styles.denseAccentOn]} />
                    <Text
                      style={[styles.denseLabel, selected && styles.denseLabelOn]}
                      numberOfLines={1}
                    >
                      {row.title}
                    </Text>
                  </>
                ) : (
                  <>
                    {row.icon ? (
                      <Icon
                        name={row.icon}
                        size={theme.typography.sizes.lg}
                        color={selected ? theme.palette.brand500 : theme.palette.ink2}
                      />
                    ) : null}
                    <View style={styles.navRowText}>
                      <Text style={styles.navRowTitle}>{row.title}</Text>
                      <Text style={styles.navRowSub}>{row.sub}</Text>
                    </View>
                    <Text
                      style={[styles.navRowChevron, selected && styles.chevronSelected]}
                    >
                      ›
                    </Text>
                  </>
                )}
              </Pressable>
            );
          })}
          {dense ? null : section.custom}
        </Fragment>
      ))}
    </View>
  );
}

function makeStyles(theme: UiTheme) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    list: { gap: s.s3 },
    sectionHeader: {
      color: p.ink2,
      fontSize: t.sizes.sm,
      fontWeight: "700",
      letterSpacing: 0.2,
      marginTop: s.s4,
      marginBottom: 2,
      marginLeft: s.s2,
    },
    navRow: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 44,
      backgroundColor: p.surface,
      borderColor: p.border,
      borderWidth: 1,
      borderRadius: r.r3,
      paddingHorizontal: s.s5,
      paddingVertical: s.s4,
      gap: s.s4,
    },
    navRowSelected: {
      // Selection is carried by the brand100 fill + accent bar; the border goes
      // neutral so it isn't a third brand marker. Colour budget S3.2.
      backgroundColor: p.brand100,
      borderColor: p.borderStrong,
    },
    // Dense = the wide master-detail nav: a flat sidebar like the console
    // rail (no card border/fill), accent bar + weight + colour on the active
    // row. Tighter rhythm than the full card menu.
    listDense: { gap: 2 },
    denseRow: {
      flexDirection: "row",
      alignItems: "center",
      minHeight: 44,
      borderRadius: r.r4,
      paddingRight: s.s4,
    },
    denseRowSelected: { backgroundColor: p.surface2 },
    denseRowPressed: { backgroundColor: p.surface2 },
    denseAccent: {
      width: 3,
      height: 20,
      borderRadius: 2,
      marginRight: s.s4,
      marginLeft: s.s1,
      backgroundColor: "transparent",
    },
    denseAccentOn: { backgroundColor: p.brand500 },
    denseLabel: { flex: 1, color: p.ink2, fontSize: t.sizes.base, fontWeight: "600" },
    denseLabelOn: { color: p.ink, fontWeight: "700" },
    navRowPressed: { backgroundColor: p.surface2 },
    navRowText: { flex: 1, gap: 2 },
    navRowTitle: { color: p.ink, fontSize: t.sizes.base, fontWeight: "600" },
    navRowSub: { color: p.ink3, fontSize: t.sizes.sm },
    navRowChevron: { color: p.ink3, fontSize: t.sizes.lg },
    chevronSelected: { color: p.ink2 }, // utility icon; selection = tint, not brand (S3.1)
  });
}
